import { __assign, __extends } from "tslib";
import { clamp, deepMix, each, get, isArray, isNil, isNumber, size } from '@antv/util';
import GroupComponent from '../abstract/group-component';
import { Trend } from '../trend/trend';
import { Handler } from './handler';
import { BACKGROUND_STYLE, BAR_STYLE, DEFAULT_HANDLER_HEIGHT, DEFAULT_HANDLER_WIDTH, DEFAULT_TREND_STYLE, FOREGROUND_STYLE, HANDLER_STYLE, MAX_TEXT_WIDTH, SLIDER_CHANGE, TEXT_SAFE_WIDTH, TEXT_STYLE, } from './constant';
import { createMask, getRectMaskAttrs, updateMask } from '../util/mask';
import { clipTextTwoLines } from '../util/util';
import { Foreground } from './foreground';
import { getCurrentPoint } from '../util/event';
var Slider = /** @class */ (function (_super) {
    __extends(Slider, _super);
    function Slider() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        // Brush 起始点坐标记录
        _this.brushPrevX = null;
        // 标识开始和结束 brush 操作
        _this.enableBrush = false;
        // Brush 框样式Shape
        _this.brushMaskShape = null;
        _this.onMouseDown = function (target) { return function (e) {
            _this.currentTarget = target;
            // 取出原生事件
            var event = e.originalEvent;
            // 2. 存储当前点击位置
            event.stopPropagation();
            event.preventDefault();
            // 兼容移动端获取数据
            var _a = getCurrentPoint(event), x = _a.x, y = _a.y;
            _this.prevX = x;
            _this.prevY = y;
            var containerDOM = _this.getContainerDOM();
            // 框选事件
            if (['background', 'foreground-brush'].includes(target)) {
                _this.brushPrevX = event.offsetX;
                _this.enableBrush = true;
                // 绑定框选事件
                containerDOM.addEventListener('mousemove', _this.onBrushMouseMove);
                containerDOM.addEventListener('mouseup', _this.onBrushMouseUp);
                containerDOM.addEventListener('mouseleave', _this.onBrushMouseUp);
                return;
            }
            // 3. 开始滑动的时候，绑定 move 和 up 事件
            containerDOM.addEventListener('mousemove', _this.onMouseMove);
            containerDOM.addEventListener('mouseup', _this.onMouseUp);
            containerDOM.addEventListener('mouseleave', _this.onMouseUp);
            // 移动端事件
            containerDOM.addEventListener('touchmove', _this.onMouseMove);
            containerDOM.addEventListener('touchend', _this.onMouseUp);
            containerDOM.addEventListener('touchcancel', _this.onMouseUp);
        }; };
        _this.onMouseMove = function (event) {
            var width = _this.cfg.width;
            var originValue = [_this.get('start'), _this.get('end')];
            // 滑动过程中，计算偏移，更新滑块，然后 emit 数据出去
            event.stopPropagation();
            event.preventDefault();
            var _a = getCurrentPoint(event), x = _a.x, y = _a.y;
            // 横向的 slider 只处理 x
            var offsetX = x - _this.prevX;
            var offsetXRange = _this.adjustOffsetRange(offsetX / width);
            // 更新 start end range 范围
            _this.updateStartEnd(offsetXRange);
            // 更新 ui
            _this.updateUI(_this.getElementByLocalId('minText'), _this.getElementByLocalId('maxText'));
            _this.prevX = x;
            _this.prevY = y;
            _this.draw();
            // 因为存储的 start、end 可能不一定是按大小存储的，所以排序一下，对外是 end >= start
            _this.emit(SLIDER_CHANGE, [_this.get('start'), _this.get('end')].sort());
            _this.delegateEmit('valuechanged', {
                originValue: originValue,
                value: [_this.get('start'), _this.get('end')],
            });
        };
        _this.onMouseUp = function () {
            // 结束之后，取消绑定的事件
            if (_this.currentTarget) {
                _this.currentTarget = undefined;
            }
            // 清空handler的高亮状态
            var handlerStyle = _this.cfg.handlerStyle;
            if (_this.minHandler && _this.minHandler.cfg.style.stroke === handlerStyle.highLightStroke) {
                _this.minHandler.update({
                    style: handlerStyle,
                });
                if (!_this.get('updateAutoRender')) {
                    _this.minHandler.render();
                }
            }
            if (_this.maxHandler && _this.maxHandler.cfg.style.stroke === handlerStyle.highLightStroke) {
                _this.maxHandler.update({
                    style: handlerStyle,
                });
                if (!_this.get('updateAutoRender')) {
                    _this.maxHandler.render();
                }
            }
            var containerDOM = _this.getContainerDOM();
            if (containerDOM) {
                containerDOM.removeEventListener('mousemove', _this.onMouseMove);
                containerDOM.removeEventListener('mouseup', _this.onMouseUp);
                // 防止滑动到 canvas 外部之后，状态丢失
                containerDOM.removeEventListener('mouseleave', _this.onMouseUp);
                // 移动端事件
                containerDOM.removeEventListener('touchmove', _this.onMouseMove);
                containerDOM.removeEventListener('touchend', _this.onMouseUp);
                containerDOM.removeEventListener('touchcancel', _this.onMouseUp);
            }
        };
        _this.onBrushMouseMove = function (event) {
            if (!_this.enableBrush)
                return;
            var x = _this.getBBox().x;
            var curX = event.offsetX;
            // 绘制 brush mask
            if (!isNumber(_this.brushPrevX)) {
                _this.brushPrevX = curX;
            }
            var maskAttrs = getRectMaskAttrs({
                x: _this.brushPrevX - x,
                y: 0,
            }, {
                x: curX - x,
                y: _this.cfg.height,
            });
            if (!_this.brushMaskShape) {
                // 新建 brush mask
                _this.brushMaskShape = createMask(_this.get('group'), 'rect', maskAttrs);
            }
            else {
                // 更新 brush mask
                updateMask(_this.brushMaskShape, maskAttrs);
            }
        };
        _this.onBrushMouseUp = function (event) {
            if (!_this.enableBrush)
                return;
            // 没有拖动偏移量时，取消框选操作
            var curX = event.offsetX;
            if (_this.brushPrevX === curX) {
                _this.cancelBrush();
                return;
            }
            var x = _this.getBBox().x;
            var width = _this.cfg.width;
            var start = (Math.min(curX, _this.brushPrevX) - x) / width;
            var end = (Math.max(curX, _this.brushPrevX) - x) / width;
            var originValue = [_this.get('start'), _this.get('end')];
            // 更新 start end
            _this.set('start', start);
            _this.set('end', end);
            // 更新 ui
            _this.updateUI(_this.getElementByLocalId('minText'), _this.getElementByLocalId('maxText'));
            _this.draw();
            _this.emit(SLIDER_CHANGE, [_this.get('start'), _this.get('end')].sort());
            _this.delegateEmit('valuechanged', {
                originValue: originValue,
                value: [_this.get('start'), _this.get('end')],
            });
            // 销毁当前 brush mask 图形
            _this.cancelBrush();
        };
        return _this;
    }
    Slider.prototype.setRange = function (min, max) {
        this.set('minLimit', min);
        this.set('maxLimit', max);
        var oldStart = this.get('start');
        var oldEnd = this.get('end');
        var newStart = clamp(oldStart, min, max);
        var newEnd = clamp(oldEnd, min, max);
        if (!this.get('isInit') && (oldStart !== newStart || oldEnd !== newEnd)) {
            this.setValue([newStart, newEnd]);
        }
    };
    Slider.prototype.getRange = function () {
        return {
            min: this.get('minLimit') || 0,
            max: this.get('maxLimit') || 1,
        };
    };
    Slider.prototype.setValue = function (value) {
        var range = this.getRange();
        if (isArray(value) && value.length === 2) {
            var originValue = [this.get('start'), this.get('end')];
            this.update({
                start: clamp(value[0], range.min, range.max),
                end: clamp(value[1], range.min, range.max),
            });
            if (!this.get('updateAutoRender')) {
                this.render();
            }
            this.delegateEmit('valuechanged', {
                originValue: originValue,
                value: value,
            });
        }
    };
    Slider.prototype.getValue = function () {
        return [this.get('start'), this.get('end')];
    };
    Slider.prototype.getDefaultCfg = function () {
        var cfg = _super.prototype.getDefaultCfg.call(this);
        return __assign(__assign({}, cfg), { name: 'slider', x: 0, y: 0, width: 100, height: 16, backgroundStyle: {}, foregroundStyle: {}, handlerStyle: {}, textStyle: {}, barStyle: {}, defaultCfg: {
                backgroundStyle: BACKGROUND_STYLE,
                foregroundStyle: FOREGROUND_STYLE,
                handlerStyle: HANDLER_STYLE,
                textStyle: TEXT_STYLE,
                barStyle: BAR_STYLE,
            } });
    };
    Slider.prototype.update = function (cfg) {
        var start = cfg.start, end = cfg.end;
        var validCfg = __assign({}, cfg);
        if (!isNil(start)) {
            validCfg.start = clamp(start, 0, 1);
        }
        if (!isNil(end)) {
            validCfg.end = clamp(end, 0, 1);
        }
        _super.prototype.update.call(this, validCfg);
        this.foreground = this.getChildComponentById(this.getElementId('foreground'));
        this.minHandler = this.getChildComponentById(this.getElementId('minHandler'));
        this.maxHandler = this.getChildComponentById(this.getElementId('maxHandler'));
        this.trend = this.getChildComponentById(this.getElementId('trend'));
    };
    Slider.prototype.init = function () {
        this.set('start', clamp(this.get('start'), 0, 1));
        this.set('end', clamp(this.get('end'), 0, 1));
        _super.prototype.init.call(this);
    };
    Slider.prototype.render = function () {
        _super.prototype.render.call(this);
        this.updateUI(this.getElementByLocalId('minText'), this.getElementByLocalId('maxText'));
    };
    Slider.prototype.renderInner = function (group) {
        var _a = this.cfg, start = _a.start, end = _a.end, width = _a.width, height = _a.height, _b = _a.trendCfg, trendCfg = _b === void 0 ? {} : _b, minText = _a.minText, maxText = _a.maxText, _c = _a.backgroundStyle, backgroundStyle = _c === void 0 ? {} : _c, _d = _a.foregroundStyle, foregroundStyle = _d === void 0 ? {} : _d, _e = _a.textStyle, textStyle = _e === void 0 ? {} : _e, _f = _a.barStyle, barStyle = _f === void 0 ? {} : _f;
        var min = start * width;
        var max = end * width;
        // default 趋势图数据
        var defaultTrendStyle = deepMix({}, DEFAULT_TREND_STYLE, trendCfg);
        if (size(get(trendCfg, 'data'))) {
            this.trend = this.addComponent(group, __assign({ component: Trend, id: this.getElementId('trend'), x: 0, y: 0, width: width,
                height: height }, defaultTrendStyle));
        }
        // 1. 背景
        this.addShape(group, {
            id: this.getElementId('background'),
            type: 'rect',
            attrs: __assign({ x: 0, y: 0, width: width,
                height: height, cursor: 'crosshair' }, backgroundStyle),
        });
        // 2. 前景 选中背景框
        this.foreground = this.addComponent(group, {
            component: Foreground,
            id: this.getElementId('foreground'),
            name: 'foreground',
            height: height,
            foregroundStyle: foregroundStyle,
            barStyle: barStyle,
        });
        // 3. 左右文字
        var minTextShape = this.addShape(group, {
            id: this.getElementId('minText'),
            type: 'text',
            attrs: __assign({ 
                // x: 0,
                y: height / 2, textAlign: 'right', text: minText, silent: false }, textStyle),
        });
        var maxTextShape = this.addShape(group, {
            id: this.getElementId('maxText'),
            type: 'text',
            attrs: __assign({ 
                // x: 0,
                y: height / 2, textAlign: 'left', text: maxText, silent: false }, textStyle),
        });
        // 滑块相关的大小信息
        var handlerStyle = deepMix({}, HANDLER_STYLE, this.cfg.handlerStyle);
        var handlerWidth = get(handlerStyle, 'width', DEFAULT_HANDLER_WIDTH);
        var handlerHeight = get(handlerStyle, 'height', DEFAULT_HANDLER_HEIGHT);
        // 4. 左右滑块
        this.minHandler = this.addComponent(group, {
            component: Handler,
            id: this.getElementId('minHandler'),
            name: 'handler-min',
            x: 0,
            y: (height - handlerHeight) / 2,
            width: handlerWidth,
            height: handlerHeight,
            cursor: 'ew-resize',
            style: handlerStyle,
            sliderHeight: height,
        });
        this.maxHandler = this.addComponent(group, {
            component: Handler,
            id: this.getElementId('maxHandler'),
            name: 'handler-max',
            x: 0,
            y: (height - handlerHeight) / 2,
            width: handlerWidth,
            height: handlerHeight,
            cursor: 'ew-resize',
            style: handlerStyle,
            sliderHeight: height,
        });
    };
    Slider.prototype.applyOffset = function () {
        this.moveElementTo(this.get('group'), {
            x: this.get('x'),
            y: this.get('y'),
        });
    };
    Slider.prototype.initEvent = function () {
        this.bindEvents();
    };
    Slider.prototype.updateUI = function (minTextShape, maxTextShape) {
        var _a = this.cfg, start = _a.start, end = _a.end, width = _a.width, minText = _a.minText, maxText = _a.maxText, handlerStyle = _a.handlerStyle, height = _a.height, barStyle = _a.barStyle;
        var min = start * width;
        var max = end * width;
        if (this.trend) {
            this.trend.update({
                width: width,
                height: height,
            });
            if (!this.get('updateAutoRender')) {
                this.trend.render();
            }
        }
        if (this.foreground) {
            var newBarStyle = ['foreground-bar', 'foreground'].includes(this.currentTarget)
                ? __assign(__assign({}, barStyle), { fill: barStyle.highLightFill }) : barStyle;
            this.foreground.update({
                x: min,
                width: max - min,
                barStyle: newBarStyle,
            });
            if (!this.get('updateAutoRender')) {
                this.foreground.render();
            }
        }
        // 滑块相关的大小信息
        var handlerWidth = get(handlerStyle, 'width', DEFAULT_HANDLER_WIDTH);
        // 设置文本
        minTextShape.attr('text', minText);
        maxTextShape.attr('text', maxText);
        var _b = this._dodgeText([min, max], minTextShape, maxTextShape), minAttrs = _b[0], maxAttrs = _b[1];
        // 2. 左侧滑块和文字位置
        if (this.minHandler) {
            var newStyle = this.currentTarget === 'minHandler'
                ? __assign(__assign({}, handlerStyle), { stroke: handlerStyle.highLightStroke }) : handlerStyle;
            this.minHandler.update({
                x: min - handlerWidth / 2,
                style: newStyle,
            });
            if (!this.get('updateAutoRender')) {
                this.minHandler.render();
            }
        }
        each(minAttrs, function (v, k) { return minTextShape.attr(k, v); });
        // 3. 右侧滑块和文字位置
        if (this.maxHandler) {
            var newStyle = this.currentTarget === 'maxHandler'
                ? __assign(__assign({}, handlerStyle), { stroke: handlerStyle.highLightStroke }) : handlerStyle;
            this.maxHandler.update({
                x: max - handlerWidth / 2,
                style: newStyle,
            });
            if (!this.get('updateAutoRender')) {
                this.maxHandler.render();
            }
        }
        each(maxAttrs, function (v, k) { return maxTextShape.attr(k, v); });
    };
    Slider.prototype.bindEvents = function () {
        var group = this.get('group');
        group.on('handler-min:mousedown', this.onMouseDown('minHandler'));
        group.on('handler-min:touchstart', this.onMouseDown('minHandler'));
        // 2. 右滑块的滑动
        group.on('handler-max:mousedown', this.onMouseDown('maxHandler'));
        group.on('handler-max:touchstart', this.onMouseDown('maxHandler'));
        // 3.背景区域框选
        var background = group.findById(this.getElementId('background'));
        background.on('mousedown', this.onMouseDown('background'));
        // 4. 前景选中区域
        // 选中区域拖拽
        group.on('foreground-scroll:mousedown', this.onMouseDown('foreground'));
        group.on('foreground-bar:mousedown', this.onMouseDown('foreground'));
        // 选中区域框选
        group.on('foreground-brush:mousedown', this.onMouseDown('foreground-brush'));
        // 移动端：只允许拖拽
        group.on('foreground:touchstart', this.onMouseDown('foreground'));
    };
    /**
     * 调整 offsetRange，因为一些范围的限制
     * @param offsetRange
     */
    Slider.prototype.adjustOffsetRange = function (offsetRange) {
        var _a = this.cfg, start = _a.start, end = _a.end;
        // 针对不同的滑动组件，处理的方式不同
        switch (this.currentTarget) {
            case 'minHandler': {
                var min = 0 - start;
                var max = 1 - start;
                return Math.min(max, Math.max(min, offsetRange));
            }
            case 'maxHandler': {
                var min = 0 - end;
                var max = 1 - end;
                return Math.min(max, Math.max(min, offsetRange));
            }
            case 'foreground-brush':
            case 'foreground': {
                var min = 0 - start;
                var max = 1 - end;
                return Math.min(max, Math.max(min, offsetRange));
            }
        }
    };
    Slider.prototype.updateStartEnd = function (offsetRange) {
        var _a = this.cfg, start = _a.start, end = _a.end;
        // 操作不同的组件，反馈不一样
        switch (this.currentTarget) {
            case 'minHandler':
                start += offsetRange;
                break;
            case 'maxHandler':
                end += offsetRange;
                break;
            case 'foreground':
            case 'foreground-brush':
                start += offsetRange;
                end += offsetRange;
                break;
        }
        this.set('start', start);
        this.set('end', end);
    };
    /**
     * 调整 text 的位置，自动躲避
     * 根据位置，调整返回新的位置
     * @param range
     */
    Slider.prototype._dodgeText = function (range, minTextShape, maxTextShape) {
        var _a, _b;
        var _c;
        var dodgeText = get(this.cfg.textStyle, 'dodgeText', null);
        if (dodgeText) {
            return dodgeText(this.cfg, range, minTextShape, maxTextShape);
        }
        var handlerWidth = get(this.cfg.handlerStyle, 'width', DEFAULT_HANDLER_WIDTH);
        var textPadding = get(this.cfg.textStyle, 'padding');
        var min = range[0], max = range[1];
        var sorted = false;
        // 如果交换了位置，则对应的 min max 也交互
        if (min > max) {
            _a = [max, min], min = _a[0], max = _a[1];
            _b = [maxTextShape, minTextShape], minTextShape = _b[0], maxTextShape = _b[1];
            sorted = true;
        }
        // 避让规则，优先显示在两侧，只有显示不下的时候，才显示在中间
        var minBBox = minTextShape.getBBox();
        var maxBBox = maxTextShape.getBBox();
        var _d = this.setTextAttrs(minBBox, minTextShape, [min, max], 'min'), minAttrs = _d.attrs, minTextWidth = _d.textWidth;
        var _e = this.setTextAttrs(maxBBox, maxTextShape, [min, max], 'max'), maxAttrs = _e.attrs, maxTextWidth = _e.textWidth;
        // 两端文字存在安全间距为4px, 如果小于该距离则两端均不显示文字
        var gap = this.cfg.width - handlerWidth * 2 - textPadding * 2 - minTextWidth - maxTextWidth;
        if (gap <= TEXT_SAFE_WIDTH) {
            // text: '' 不显示文字
            var newMinAttrs = __assign(__assign({}, minAttrs), { text: '' });
            var newMaxAttrs = __assign(__assign({}, maxAttrs), { text: '' });
            return !sorted ? [newMinAttrs, newMaxAttrs] : [newMaxAttrs, newMinAttrs];
        }
        // 一侧不滑动(文字仍显示在中间)，另一侧滑动的情况下。
        // 检测中间宽度是否能展示当前不滑动文字的文本，如果距离不够，则隐藏不滑动一侧的文字。
        if (minAttrs.textAlign === 'left' || maxAttrs.textAlign === 'right') {
            var foregroundWidth = (_c = this.foreground) === null || _c === void 0 ? void 0 : _c.get('width');
            var gap_1 = foregroundWidth - handlerWidth - textPadding * 2;
            if (minAttrs.textAlign === 'left') {
                if (gap_1 <= minTextWidth) {
                    return !sorted ? [__assign(__assign({}, minAttrs), { text: '' }), __assign({}, maxAttrs)] : [__assign({}, maxAttrs), __assign(__assign({}, minAttrs), { text: '' })];
                }
                else {
                    return !sorted ? [minAttrs, maxAttrs] : [maxAttrs, minAttrs];
                }
            }
            if (maxAttrs.textAlign === 'right') {
                if (gap_1 <= maxTextWidth) {
                    return !sorted ? [__assign({}, minAttrs), __assign(__assign({}, maxAttrs), { text: '' })] : [__assign(__assign({}, maxAttrs), { text: '' }), __assign({}, minAttrs)];
                }
                else {
                    return !sorted ? [minAttrs, maxAttrs] : [maxAttrs, minAttrs];
                }
            }
        }
        return !sorted ? [minAttrs, maxAttrs] : [maxAttrs, minAttrs];
    };
    Slider.prototype.setTextAttrs = function (bbox, textShape, range, direction) {
        var handlerWidth = get(this.cfg.handlerStyle, 'width', DEFAULT_HANDLER_WIDTH);
        var textPadding = get(this.cfg.textStyle, 'padding');
        var gapWidth = handlerWidth / 2 + textPadding;
        var min = range[0], max = range[1];
        var width = bbox.width;
        var text = textShape.attr('text');
        var font = textShape.attr('font');
        var textWidth = 0;
        var attrs = {};
        // 字体长度>最大长度，换行处理
        if (width > MAX_TEXT_WIDTH) {
            textWidth = MAX_TEXT_WIDTH;
            if (direction === 'min') {
                attrs =
                    // 如果 最大单行宽度 > 左侧能塞下的宽度，则放在左侧
                    MAX_TEXT_WIDTH > min - gapWidth
                        ? {
                            x: min + gapWidth,
                            textAlign: 'left',
                            text: clipTextTwoLines(text, MAX_TEXT_WIDTH, font),
                        }
                        : {
                            x: min - gapWidth,
                            textAlign: 'right',
                            text: clipTextTwoLines(text, MAX_TEXT_WIDTH, font),
                        };
            }
            else {
                attrs =
                    MAX_TEXT_WIDTH > this.cfg.width - max - gapWidth
                        ? {
                            x: max - gapWidth,
                            textAlign: 'right',
                            text: clipTextTwoLines(text, MAX_TEXT_WIDTH, font),
                        }
                        : {
                            x: max + gapWidth,
                            textAlign: 'left',
                            text: clipTextTwoLines(text, MAX_TEXT_WIDTH, font),
                        };
            }
        }
        else {
            textWidth = width;
            if (direction === 'min') {
                attrs =
                    width > min - gapWidth
                        ? {
                            x: min + gapWidth,
                            textAlign: 'left',
                        }
                        : {
                            x: min - gapWidth,
                            textAlign: 'right',
                        };
            }
            else {
                attrs =
                    width > this.cfg.width - max - gapWidth
                        ? {
                            x: max - gapWidth,
                            textAlign: 'right',
                        }
                        : {
                            x: max + gapWidth,
                            textAlign: 'left',
                        };
            }
        }
        return {
            attrs: attrs,
            textWidth: textWidth,
        };
    };
    Slider.prototype.draw = function () {
        var container = this.get('container');
        var canvas = container && container.get('canvas');
        if (canvas) {
            canvas.draw();
        }
    };
    Slider.prototype.getContainerDOM = function () {
        var container = this.get('container');
        var canvas = container && container.get('canvas');
        return canvas && canvas.get('container');
    };
    Slider.prototype.cancelBrush = function () {
        // 销毁当前 brush mask 图形
        if (this.brushMaskShape) {
            this.brushMaskShape.remove();
            this.brushMaskShape = null;
        }
        this.enableBrush = false;
        this.brushMaskShape = null;
    };
    return Slider;
}(GroupComponent));
export { Slider };
export default Slider;
//# sourceMappingURL=slider.js.map